import React, { useState } from 'react';

import { useEffectChange, useScreenMediaQuery } from '@shared/hooks';

import { dayjs } from '@/utils/dateTimeUtils';
import { Button, TimePicker, DateRangePicker } from '@/widgets';
import { sendClickEvent } from '@/utils/AnalyticsUtils';
import constants from '@/utils/constants';
import {
	HOME_DATE_FILTER_TIME_OPENED,
	HOME_DATE_FILTER_TIME_OK_CLICKED,
} from '@/constants';
import { TEST_IDS } from '@/constants/testConstants';

import styles from './CustomDateRangePicker.module.scss';
import CustomDateRangePickerMobile from './CustomDateRangePickerMobile';

const {
	FILTERS_DISPLAY_DATE_FORMAT,
	FILTERS_FUNCTIONAL_DATE_FORMAT,
	FILTERS_TIME_FORMAT,
} = constants;
const { CUSTOM_DATE_RANGE_PICKER } = TEST_IDS;
const { DATE_PICKER_FOOTER_CANCEL, DATE_PICKER_FOOTER_APPLY_BUTTON } = TEST_IDS;
// defaultPickerValue is used to show prev month & current month in view if the date is in same month
const getDefaultPickerValues = (startDate) => {
	const startMonth = dayjs().isSame(dayjs(startDate), 'month')
		? dayjs().subtract(1, 'month')
		: dayjs(startDate);
	return [startMonth, undefined];
};

const CustomDateRangePicker = (props) => {
	const {
		startDate,
		endDate,
		showTimePicker = true,
		container = '',
		onApplyClick,
		onCancelClick,
		// activeDateRange is used to show enabled range for selection
		activeDateRange = [dayjs().subtract(180, 'days').startOf('day'), dayjs()],
	} = props;
	const isMobile = useScreenMediaQuery('maxMobile');

	const [localStartDate, setLocalStartDate] = useState(dayjs(startDate));
	const [localEndDate, setLocalEndDate] = useState(dayjs(endDate));

	useEffectChange(() => {
		setLocalStartDate(dayjs(startDate));
		setLocalEndDate(dayjs(endDate));
	}, [startDate, endDate]);

	const _getCalendarContainer = () =>
		document.getElementById('dateTimeRangePickerContainer');
	const _getStartTimePickerContainer = () =>
		document.getElementById('startTimePicker');
	const _getEndTimePickerContainer = () =>
		document.getElementById('endTimePicker');

	const getContainerWiseEventName = (eventName) => {
		if (container === 'home') {
			switch (eventName) {
				case 'DATE_FILTER_TIME_OPENED':
					return HOME_DATE_FILTER_TIME_OPENED;
				case 'DATE_FILTER_TIME_OK_CLICKED':
					return HOME_DATE_FILTER_TIME_OK_CLICKED;
				default:
					return eventName;
			}
		}
		return eventName;
	};

	const disabledDate = (current) => {
		// Can not select days before activeFrom and activeTo
		let activeFrom = activeDateRange[0];
		let activeTo = activeDateRange[1].endOf('day');

		return current < activeFrom || current > activeTo;
	};

	const createDate = (obj, type) => {
		const { date, time } = obj;
		let _date = '';
		let _time = '';

		if (date) {
			_date = dayjs(date).format(FILTERS_FUNCTIONAL_DATE_FORMAT);
			_time = dayjs(
				type === 'from' ? localStartDate.startOf('day') : localEndDate,
			).format(FILTERS_TIME_FORMAT);
		} else {
			_time = dayjs(time).format(FILTERS_TIME_FORMAT);
			_date = dayjs(type === 'from' ? localStartDate : localEndDate).format(
				FILTERS_FUNCTIONAL_DATE_FORMAT,
			);
		}

		return dayjs(`${_date} ${_time}`);
	};

	const _handleCalendarChange = (ranges) => {
		const [startDate, endDate] = ranges;
		if (!startDate || !endDate) {
			return;
		}
		const from = createDate({ date: startDate }, 'from');
		let to = createDate({ date: endDate }, 'to');
		// The below if condition is to check if the end date is before today's date then setting time to end of the day
		if (dayjs(to).isBefore(dayjs().startOf('day'))) {
			to = dayjs(endDate).endOf('day');
		} else {
			to = dayjs();
		}
		setLocalStartDate(from);
		setLocalEndDate(to);
	};

	const _handleStartTimeChange = (time) => {
		sendClickEvent({
			name: getContainerWiseEventName('DATE_FILTER_TIME_OK_CLICKED'),
			value: 'startTime',
		});
		const from = createDate({ time }, 'from');
		setLocalStartDate(from);
	};

	const _handleEndTimeChange = (time) => {
		sendClickEvent({
			name: getContainerWiseEventName('DATE_FILTER_TIME_OK_CLICKED'),
			value: 'endTime',
		});
		const to = createDate({ time }, 'to');
		setLocalEndDate(to);
	};

	const handleApplyClick = () => {
		onApplyClick({
			fromDate: localStartDate,
			toDate: localEndDate,
			selectedDateType: 'custom',
		});
	};

	const _handleTimePickerOpenChange = (isOpen, timerType) => {
		if (isOpen)
			sendClickEvent({
				name: getContainerWiseEventName('DATE_FILTER_TIME_OPENED'),
				value: timerType,
			});
	};

	const _renderTimePicker = () => (
		<>
			<div className={styles.timePicker} id="startTimePicker">
				<span>Start Time: </span>
				<TimePicker
					inputReadOnly
					getPopupContainer={_getStartTimePickerContainer}
					onChange={_handleStartTimeChange}
					onOpenChange={(isOpen) =>
						_handleTimePickerOpenChange(isOpen, 'startTime')
					}
					value={dayjs(dayjs(localStartDate), FILTERS_TIME_FORMAT)}
					showNow={false}
					format={FILTERS_TIME_FORMAT}
					allowClear={false}
					popupClassName={styles.timePickerPopup}
				/>
			</div>
			<div className={styles.timePicker} id="endTimePicker">
				<span>End Time: </span>
				<TimePicker
					inputReadOnly
					getPopupContainer={_getEndTimePickerContainer}
					onChange={_handleEndTimeChange}
					onOpenChange={(isOpen) =>
						_handleTimePickerOpenChange(isOpen, 'endTime')
					}
					value={dayjs(dayjs(localEndDate), FILTERS_TIME_FORMAT)}
					showNow={false}
					format={FILTERS_TIME_FORMAT}
					allowClear={false}
					popupClassName={styles.timePickerPopup}
				/>
			</div>
		</>
	);

	if (isMobile) {
		return (
			<CustomDateRangePickerMobile
				{...props}
				activeDateRange={activeDateRange}
				createDate={createDate}
			/>
		);
	}

	return (
		<div data-id={CUSTOM_DATE_RANGE_PICKER}>
			<div className={styles.datePickerTitleWrapper}>
				<div className={styles.datePickerTitle}>From</div>
				<div className={styles.datePickerTitle}>To</div>
			</div>
			<div className={styles.datePickerRow}>
				<div
					id="dateTimeRangePickerContainer"
					className={styles.desktopPickerMainContainer}
				>
					<DateRangePicker
						inputReadOnly
						open
						allowClear={false}
						format={[FILTERS_DISPLAY_DATE_FORMAT, FILTERS_DISPLAY_DATE_FORMAT]}
						defaultPickerValue={getDefaultPickerValues(localStartDate)}
						placeholder={['From', 'To']}
						getPopupContainer={_getCalendarContainer}
						disabledDate={disabledDate}
						value={[dayjs(localStartDate), dayjs(localEndDate)]}
						onCalendarChange={_handleCalendarChange}
						suffixIcon={null}
						separator={null}
						className={styles.rangePickerDropdown}
						popupClassName={styles.rangePickerPopupContainer}
					/>
					{showTimePicker && (
						<div className={styles.desktopTimePickerContainer}>
							{_renderTimePicker()}
						</div>
					)}
				</div>
			</div>
			<div className={styles.footer}>
				<Button
					onClick={onCancelClick}
					className={styles.cancelButton}
					variant="tertiary"
					data-id={DATE_PICKER_FOOTER_CANCEL}
				>
					Cancel
				</Button>
				<Button
					onClick={handleApplyClick}
					className={styles.applyButton}
					variant="primary"
					data-id={DATE_PICKER_FOOTER_APPLY_BUTTON}
				>
					Apply
				</Button>
			</div>
		</div>
	);
};

export default CustomDateRangePicker;
